import React from 'react'
import {
  Modal,
  Button,
  Progress,
  Tooltip,
  Empty,
  Spin,
  Checkbox,
  Select
} from 'antd'
import {
  FiLayers,
  FiCheckCircle,
  FiZap,
  FiMinusCircle,
  FiCloudLightning,
  FiSave,
  FiCheck,
  FiRotateCw,
  FiInfo,
  FiTrash2
} from 'react-icons/fi'
import { ImagePreview } from '../../../../../../../../../components'
import { useMatchImage } from './useMatchImage'
import './MatchImage.scss'

const MatchImage = props => {
  const { onCancel } = props
  const {
    IMAGE_STYLES,
    selectedStyle,
    setSelectedStyle,
    refImages,
    handleRemoveRefImage,
    toggleRefSelection,
    isGenerating,
    progress,
    generationResults,
    currentlyGeneratingIdx,
    isApplied,
    startMultiPageGeneration,
    applyToPages,
    t
  } = useMatchImage(props)

  const renderRefImageGroup = () => {
    const grouped = refImages.reduce((acc, img) => {
      const groupKey =
        img.pageIndex !== undefined
          ? `${t('matchimage:page')} ${img.pageIndex + 1}`
          : t('matchimage:uploaded')
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(img)
      return acc
    }, {})

    return Object.entries(grouped).map(([title, images]) => (
      <div key={title} className="page-group-container">
        <div className="page-group-header">
          <span className="page-group-title">{title}</span>
          <span className="page-group-count">
            {images.length} {t('matchimage:refs')}
          </span>
        </div>
        <div className="page-group-content">
          {images.map(img => (
            <div
              key={img.id}
              className={`ref-item-card ${img.selected ? 'selected' : ''}`}
            >
              <div className="ref-item-checkbox">
                <Checkbox
                  checked={img.selected}
                  onChange={() => toggleRefSelection(img.id)}
                />
              </div>
              <div className="ref-item-preview">
                <ImagePreview src={img.preview} alt="ref" />
              </div>
              <div className="ref-item-actions">
                <Tooltip title={t('buttons:remove')}>
                  <div
                    className="delete-action"
                    onClick={() => handleRemoveRefImage(img.id)}
                  >
                    <FiTrash2 />
                  </div>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))
  }

  return (
    <Modal
      visible
      title={
        <div className="modal-header-custom">
          <div className="icon-wrapper">
            <FiLayers />
          </div>
          <div className="header-text">
            <h3>{t('matchimage:consistency')}</h3>
            <p>
              {t('matchimage:consistency_subtitle')}
            </p>
          </div>
        </div>
      }
      width={1100}
      onCancel={onCancel}
      footer={[
        <div key="footer" className="modal-footer-container">
          <Button
            key="cancel"
            className="btn-secondary"
            onClick={onCancel}
            disabled={isGenerating}
          >
            {t('matchimage:cancel')}
          </Button>
          <div className="footer-actions">
            <Button
              key="generate"
              type="primary"
              className="btn-generate"
              onClick={startMultiPageGeneration}
              loading={isGenerating}
            >
              {!isGenerating && <FiZap />}
              {t('matchimage:generate_selected_pages')}
            </Button>

            <Button
              key="apply"
              type="primary"
              className={`btn-apply ${isApplied ? 'applied' : ''}`}
              onClick={applyToPages}
              disabled={
                isGenerating || generationResults.length === 0 || isApplied
              }
            >
              {isApplied ? <FiCheck /> : <FiSave />}
              {isApplied
                ? t('matchimage:applied')
                : t('matchimage:apply_to_all_pages')}
            </Button>
          </div>
        </div>
      ]}
      centered
      className="match-image-modal"
    >
      <div className="match-image-content">
        <div className="match-image-grid">
          <div className="match-image-sidebar">
            <div className="sidebar-section">
              <div className="settings-card">
                <h5>{t('matchimage:visual_style')}</h5>
                <Select
                  value={selectedStyle}
                  onChange={setSelectedStyle}
                  className="style-dropdown-minimal"
                  style={{ width: '100%' }}
                >
                  {IMAGE_STYLES.map(style => (
                    <Select.Option key={style.value} value={style.value}>
                      {style.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="references-section">
                <div className="section-header">
                  <h5>{t('matchimage:reference_images')}</h5>
                  <Tooltip title={t('matchimage:reference_images_tip')}>
                    <FiInfo className="info-icon" />
                  </Tooltip>
                </div>
                <div className="references-scroll-area">
                  {refImages.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t('matchimage:no_references_found')}
                    />
                  ) : (
                    renderRefImageGroup()
                  )}
                </div>
                <div className="limit-hint">
                  {t('matchimage:max_9_reference_images')}
                </div>
              </div>
            </div>
          </div>

          <div className="match-image-main">
            <div className="main-display-section">
              <div className="status-header">
                <h5>{t('matchimage:generation_progress')}</h5>
                {isGenerating && <Spin size="small" />}
              </div>

              {isGenerating || generationResults.length > 0 ? (
                <div className="generation-area">
                  <div className="progress-bar-container">
                    <div className="progress-meta">
                      <span className="current-task">
                        {currentlyGeneratingIdx !== null
                          ? `${t(
                            'matchimage:generating_page'
                          )} ${currentlyGeneratingIdx + 1}...`
                          : t('matchimage:ready')}
                      </span>
                      <span className="progress-percent">{progress}%</span>
                    </div>
                    <Progress
                      percent={progress}
                      strokeWidth={8}
                      showInfo={false}
                      className="custom-progress"
                      strokeColor={{
                        '0%': '#1a73e8',
                        '100%': '#34a853'
                      }}
                      status={isGenerating ? 'active' : 'success'}
                    />
                  </div>
                  <div className="generation-grid-container">
                    <div className="generation-grid">
                      {generationResults.map(res => (
                        <div
                          key={res.pageIndex}
                          className={`generation-card ${res.status} ${currentlyGeneratingIdx === res.pageIndex
                            ? 'is-generating'
                            : ''
                            }`}
                        >
                          <div className="card-image-wrapper">
                            {res.status === 'success' ? (
                              <ImagePreview src={res.url} />
                            ) : res.status === 'error' ? (
                              <div className="error-placeholder">
                                <FiMinusCircle />
                                <span>{t('matchimage:failed')}</span>
                              </div>
                            ) : (
                              <div className="loading-placeholder">
                                <div className="shimmer-overlay" />
                                <div className="pulse-icon">
                                  <FiCloudLightning />
                                </div>
                                <span className="generating-text">
                                  {t('matchimage:generating')}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="card-info">
                            <span className="card-page-label">
                              {t('matchimage:page')} {res.pageIndex + 1}
                            </span>
                            {res.status === 'success' && (
                              <FiCheckCircle className="success-icon" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state-placeholder">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="empty-text">
                        <p>{t('matchimage:no_images_generated_yet')}</p>
                        <span>{t('matchimage:click_generate_to_start')}</span>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MatchImage
